<script setup lang="ts">
import type { FiltersQueryParams } from '~/models/Filter'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, watch } from 'vue'
import { KsButton } from '@aschehoug/kloss'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import SubjectFilter from '~/components/ResourceFinder/SubjectFilter.vue'
import GradeFilter from '~/components/ResourceFinder/GradeFilter.vue'
import HeaderFilter from '~/components/ResourceFinder/HeaderFilter.vue'
import ProductFilter from '~/components/ResourceFinder/ProductFilter.vue'
import TaskTypeFilter from '~/components/ResourceFinder/TaskTypeFilter.vue'
import ColophonButton from '~/components/ResourceFinder/ColophonButton.vue'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import useQueryParams from '~/composables/queryParams'
import useFilterSettingsStore from '~/stores/filterSettings'
import { gradesSorted } from '~/utils/gradeSorter'
import { QueryParamKey } from '~/models/QueryParamKeys'
import useGroupsStore from '~/stores/groups'
import useArrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'

const props = withDefaults(defineProps<{
  autoSelect: boolean
  enableSubjectFilter?: boolean
  enableGradeFilter?: boolean
}>(), {
  enableSubjectFilter: true,
  enableGradeFilter: true,
})

const { truthy } = useArrayUtils()
const { t } = useI18n()
const filterStore = useFilterStore()
const {
  selectedHeaderChildren,
  selectedHeaders,
  selectedGrade,
} = storeToRefs(filterStore)
const { initFilters, resetViewMode, setSelectedGrade } = filterStore
const { filterOptions } = storeToRefs(useFilterSettingsStore())
const { set, get, clear } = useQueryParams<FiltersQueryParams>(QueryParamKey.Filters)
const { userRelevantGrades, user } = storeToRefs(useAuthStore())
const { selectedGroup } = storeToRefs(useGroupsStore())
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())

const defaultGrade = computed(() => {
  if (selectedGroup.value && selectedGroup.value.grade) {
    return selectedGroup.value.grade
  }
  return userRelevantGrades.value[0] || gradesSorted[0]
})

const route = useRoute()
const queryFilter = computed(() => route.query[QueryParamKey.Filters])

watch(queryFilter, ((newFilter, oldFilter) => {
  if (newFilter === oldFilter) return
  initFilters({
    ...get(),
    [QueryParamKey.Grade]: get()[QueryParamKey.Grade] || defaultGrade.value,
  })
}))

onMounted(() => {
  nextTick(() => {
    initFilters({
      ...get(),
      [QueryParamKey.Grade]: get()[QueryParamKey.Grade] || defaultGrade.value,
    })
  })
})

const eitherOr = (a: any, b: any) => (a && !b) || (!a && b)

watch([ selectedGrade, selectedHeaders ],
([newGrade, newHeaders], [oldGrade, oldHeaders]) => {
  if (eitherOr(newGrade, oldGrade) || eitherOr(newHeaders, oldHeaders)) {
    /**
     *  Skip initialization and resetting of values, in order to only update query params if a
     * filter has changed from one value to another. Initialization is handled by both the onMounted
     * hook and the watching of the route, and the initialization is handled better by initFilters.
     */
    return
  }
  const params: Record<string, any> = {
    [QueryParamKey.Grade]: undefined,
    [QueryParamKey.Headers]: undefined,
    ...get(),
    ...(newGrade !== oldGrade ? { [QueryParamKey.Grade]: selectedGrade.value } : {} ),
  }

  params[QueryParamKey.Grade] = selectedGrade.value === defaultGrade.value ? undefined : selectedGrade.value

  if (selectedHeaders.value.length > 0) {
    params[QueryParamKey.Headers] = [...(new Set(selectedHeaders.value.map((header) => header.locationId).filter(truthy)))]
  } else {
    params[QueryParamKey.Headers] = undefined
  }

  if (Object.values(params).every(v => v === undefined)) {
    return clear()
  }

  if (Object.keys(params).length) {
    return set(params)
  }
})

watch(() => user.value?.grades, (grades) => {
  const query = get()[QueryParamKey.Grade]
  setSelectedGrade((query && (grades ?? []).includes(query)) ? query : defaultGrade.value)
})

const clearFilter = () => {
  resetViewMode()
  clear()
}
</script>

<template>
  <header
    id="resource-finder"
    class="relative mx-auto my-8 flex max-w-screen-au flex-col flex-wrap gap-4 px-4 transition-all sm:flex-row sm:items-center sm:px-8"
  >
    <SubjectFilter
      v-if="enableSubjectFilter"
      v-show="filterOptions.subject"
    />
    <GradeFilter
      v-if="enableGradeFilter"
      v-show="filterOptions.grade"
    />
    <ProductFilter v-show="filterOptions.product" />
    <HeaderFilter
      v-show="filterOptions.header"
      :index="0"
    />
    <HeaderFilter
      v-for="(headerChildren, index) in selectedHeaderChildren"
      v-show="filterOptions.header"
      :key="headerChildren[0]?.id"
      :index="index + 1"
      :resources="headerChildren"
      :auto-select="props.autoSelect"
    />
    <TaskTypeFilter v-show="filterOptions.taskType" />
    <KsButton
      v-if="selectedHeaders.length > 0"
      variant="tertiary"
      shape="rounded"
      @click="clearFilter"
    >
      {{ t('filters.emptyFilter') }}
    </KsButton>
    <ColophonButton v-if="hasLoadedProducts" />
  </header>
</template>
